import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, Wrench, Home, TreeDeciduous, Camera, Upload, X, MapPin, Plus, Minus,
  ArrowRight, ArrowLeft, CalendarDays, CheckCircle2, Circle,
} from "lucide-react";
import config from "../../config";
import { useCityPrice } from "../../hooks/useCityPrice";
import { DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD     = 3;

const WIRING_TYPE_ICONS = { new: Sparkles, old: Wrench };
const LOCATION_ICONS = { internal: Home, external: TreeDeciduous };

// Custom "More" metre counts aren't in the catalog — computed live from the
// same base+per-metre formula the 6/10/20/30 catalog tiers follow. The
// spec's 6-metre minimum rule applies here too (billable = MAX(requested, 6)).
const customLengthBasePrice = (meters) => 349 + Math.max(0, meters - 6) * 25;

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const STEPS = [
  { key: "wiring",   label: "Wiring",      sub: "Type, location, points & length" },
  { key: "details",  label: "Details",     sub: "Photo, note & address"           },
  { key: "schedule", label: "Date & Time", sub: "Book your visit"                 },
];
const WIRING_SUBSTEPS = ["type", "location", "points", "length"];

const WiringBookingPage = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const service = useMemo(
    () => config.SERVICE_CONFIG?.technical?.services?.["electrical-services"]?.types?.find((t) => t.value === "wiring"),
    []
  );
  const lengthOptions = service?.subCategories?.[0]?.types || [];
  const pointsCatalog = service?.points || [];

  const [step, setStep] = useState("type");
  const [wiringTypeKey, setWiringTypeKey] = useState(null);
  const [location, setLocation] = useState(null); // "internal" | "external"
  const [selectedPoints, setSelectedPoints] = useState({}); // { [pointKey]: qty }
  const [lengthOption, setLengthOption] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customLengthValue, setCustomLengthValue] = useState("");
  const [customMeters, setCustomMeters] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [note, setNote] = useState("");

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const stepIndex = STEPS.findIndex((s) => s.key === (WIRING_SUBSTEPS.includes(step) ? "wiring" : step));
  const wiringType = service?.workTypes?.find((wt) => wt.key === wiringTypeKey) || null;
  const isLengthNotSure = lengthOption?.notSure;

  const totalPointQty = Object.values(selectedPoints).reduce((sum, q) => sum + q, 0);
  const pointsList = pointsCatalog.filter((p) => selectedPoints[p.key] > 0);

  const cityBasePrice = (value, basePrice) => cityPrice(value, basePrice);
  const wiringBase = (() => {
    if (isLengthNotSure || !lengthOption) return 0;
    if (customMeters) return cityBasePrice(`wiring_custom_${customMeters}`, customLengthBasePrice(customMeters));
    return cityBasePrice(lengthOption.value, lengthOption.price);
  })();
  const labour = isLengthNotSure ? 0 : Math.round(wiringBase * (wiringType?.multiplier || 1));
  const material = isLengthNotSure ? 0 : Math.round(wiringBase * (wiringType?.materialFactor || 0));
  const pointsAddOn = isLengthNotSure ? 0 : totalPointQty * (service?.perPointPrice || 0);
  const subtotal = isLengthNotSure ? cityBasePrice("wiring_inspection", 149) : (labour + material + pointsAddOn);
  const tax = Number(((subtotal * 18) / 100).toFixed(2));
  const surcharge = Number(((subtotal * 5) / 100).toFixed(2));
  const grandTotal = Number((subtotal + tax + surcharge).toFixed(2));
  const hasStartingPrice = wiringTypeKey === "new" || location === "external" || isLengthNotSure;

  const billableMeters = customMeters ? Math.max(6, customMeters) : (lengthOption?.meters ?? null);

  const chooseWiringType = (key) => {
    setWiringTypeKey(key);
    setStep("location");
  };

  const chooseLocation = (loc) => {
    setLocation(loc);
    setStep("points");
  };

  const updatePointQty = (key, delta) => {
    setSelectedPoints((prev) => {
      const next = Math.max(0, Math.min(20, (prev[key] || 0) + delta));
      const updated = { ...prev, [key]: next };
      if (next === 0) delete updated[key];
      return updated;
    });
  };

  const confirmPoints = () => {
    if (totalPointQty === 0) return;
    setStep("length");
  };

  const chooseLength = (leaf) => {
    if (leaf.value === "wiring_other") {
      setShowCustomInput(true);
      return;
    }
    setLengthOption(leaf);
    setCustomMeters(null);
    setStep("details");
  };

  const confirmCustomLength = () => {
    const n = parseInt(customLengthValue, 10);
    if (!n || n <= 0) return;
    setLengthOption({ value: "wiring_other", label: "More" });
    setCustomMeters(n);
    setStep("details");
  };

  const handlePhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const goBack = () => {
    if (step === "schedule") { setStep("details"); return; }
    if (step === "details") { setStep("length"); setShowCustomInput(false); return; }
    if (step === "length") { setStep("points"); setShowCustomInput(false); return; }
    if (step === "points") { setStep("location"); return; }
    if (step === "location") { setWiringTypeKey(null); setStep("type"); return; }
    navigate("/schedule-booking/technical");
  };

  const goToSchedule = async () => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const lengthLabel = () => {
    if (isLengthNotSure) return "Not Sure — Inspection Required";
    if (customMeters) return `${billableMeters} m${customMeters < 6 ? " (6m minimum applied)" : ""}`;
    return lengthOption?.label || "";
  };

  const pointsSummary = pointsList.map((p) => `${p.label} × ${selectedPoints[p.key]}`).join(", ");

  const handleCheckout = async () => {
    if (!selectedDate || !selectedTime || !wiringType) return;
    setCheckoutLoading(true);

    try {
      const locLabel = location === "internal" ? "Internal" : "External";
      const title = `${wiringType.label} — ${locLabel} — ${lengthLabel()}`;
      const cartItem = {
        id: `wiring-${wiringTypeKey}-${location}-${lengthOption?.value || "notsure"}-${customMeters || ""}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
        title,
        name: title,
        short: [
          pointsSummary || null,
          photoDataUrl ? "Photo attached" : null,
          note.trim() || null,
          hasStartingPrice ? "Starting price — technician confirms final requirement after inspection" : null,
        ].filter(Boolean).join(" · ") || "No additional notes",
        subtitle: "1 unit",
        category: "Wiring",
        price: subtotal,
        image: service?.image || DEFAULT_HERO_IMAGE,
        qty: 1,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: "technical",
      };

      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItem.id) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, cartItem]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      // Back to the shared technical-services cart-building page (already
      // written to the shared cart above) instead of straight to checkout,
      // so other services can still be added before paying once for all.
      if (isUserLoggedIn()) {
        navigate("/schedule-booking/technical");
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/schedule-booking/technical" } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!service) {
    return <div style={S.page}><div style={S.container}><div style={S.errorBox}>Wiring service is not configured.</div></div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/schedule-booking/technical" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>Wiring</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><CalendarDays size={13} /> WIRING BOOKING</div>
          <h1 style={S.heroTitle}>Book Electrical Wiring — New or Old</h1>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── New or Old Wiring ────────────────────────────────────────────── */}
        {step === "type" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Electrical Wiring</h2>
            <p style={S.sectionSub}>New wiring or an existing one that needs work?</p>
            <div style={S.workTypeGrid}>
              {service.workTypes.map((wt) => {
                const Icon = WIRING_TYPE_ICONS[wt.key] || Wrench;
                return (
                  <button key={wt.key} type="button" style={S.workTypeCard} onClick={() => chooseWiringType(wt.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={22} color="#16a34a" /></div>
                    <div style={S.workTypeBody}>
                      <div style={S.workTypeLabel}>{wt.label}</div>
                      {wt.description && <div style={S.workTypeDesc}>{wt.description}</div>}
                    </div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Internal or External ─────────────────────────────────────────── */}
        {step === "location" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Where Is the Wiring?</h2>
            <p style={S.sectionSub}>{wiringType?.label}</p>
            <div style={S.workTypeGrid}>
              {[
                { key: "internal", label: "Internal", description: "Inside the house, office, shop, or building." },
                { key: "external", label: "External", description: "Outside the building / external wall / outdoor area." },
              ].map((loc) => {
                const Icon = LOCATION_ICONS[loc.key];
                return (
                  <button key={loc.key} type="button" style={S.workTypeCard} onClick={() => chooseLocation(loc.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={20} color="#16a34a" /></div>
                    <div style={S.workTypeBody}>
                      <div style={S.workTypeLabel}>{loc.label}</div>
                      <div style={S.workTypeDesc}>{loc.description}</div>
                    </div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── What Do You Need? (multi-select points) ───────────────────────── */}
        {step === "points" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What Do You Need?</h2>
            <p style={S.sectionSub}>Select all the points you need wired — you can pick more than one</p>
            <div style={S.pointsGrid}>
              {pointsCatalog.map((p) => {
                const qty = selectedPoints[p.key] || 0;
                return (
                  <div key={p.key} style={{ ...S.pointCard, ...(qty > 0 ? S.pointCardActive : {}) }}>
                    <img
                      src={p.image || DEFAULT_HERO_IMAGE}
                      alt={p.label}
                      style={S.pointImg}
                      onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                    />
                    <div style={S.pointBody}>
                      <div style={S.typeCheckRow}>
                        {qty > 0 ? <CheckCircle2 size={16} color="#16a34a" /> : <Circle size={16} color="#d1d5db" />}
                        <span style={S.typeName}>{p.label}</span>
                      </div>
                      <div style={S.qtyRow}>
                        <button
                          type="button"
                          style={qty === 0 ? S.qtyBtnDisabled : S.qtyBtn}
                          onClick={() => qty > 0 && updatePointQty(p.key, -1)}
                          disabled={qty === 0}
                        >
                          <Minus size={13} />
                        </button>
                        <span style={S.qtyText}>{qty}</span>
                        <button type="button" style={S.qtyBtnP} onClick={() => updatePointQty(p.key, 1)}>
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              style={{ ...S.continueBtn, ...(totalPointQty === 0 ? S.continueBtnDisabled : {}) }}
              disabled={totalPointQty === 0}
              onClick={confirmPoints}
            >
              Continue ({totalPointQty}) <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Wiring Length ─────────────────────────────────────────────────── */}
        {step === "length" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>📏 How Much Wiring?</h2>
            <p style={S.sectionSub}>Wiring charges start from a minimum of 6 metres — less is still billed at 6m</p>
            <div style={S.pointsGrid}>
              {lengthOptions.map((leaf) => (
                <button key={leaf.value} type="button" style={S.lengthCard} onClick={() => chooseLength(leaf)}>
                  {leaf.label}
                </button>
              ))}
            </div>
            {showCustomInput && (
              <div style={S.customPointsRow}>
                <input
                  type="number"
                  min="1"
                  style={S.customPointsInput}
                  placeholder="Enter metres needed"
                  value={customLengthValue}
                  onChange={(e) => setCustomLengthValue(e.target.value)}
                />
                <button
                  type="button"
                  style={{ ...S.continueBtn, ...S.noteFooterBtn, ...(!customLengthValue ? S.continueBtnDisabled : {}) }}
                  disabled={!customLengthValue}
                  onClick={confirmCustomLength}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Add Details: Photo + Note + Location ──────────────────────────── */}
        {step === "details" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Add Details</h2>
            <p style={S.sectionSub}>
              {isLengthNotSure
                ? "A photo is strongly recommended — our electrician will check the requirement"
                : "Optional — helps the electrician come prepared"}
            </p>
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={S.hiddenInput} onChange={handlePhotoFile} />
            <input ref={photoInputRef} type="file" accept="image/*" style={S.hiddenInput} onChange={handlePhotoFile} />
            {photoDataUrl ? (
              <div style={S.photoPreviewRow}>
                <img src={photoDataUrl} alt="Attached" style={S.photoPreview} />
                <button type="button" style={S.addedRemoveBtn} onClick={() => setPhotoDataUrl("")}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div style={S.photoBtnRow}>
                <button type="button" style={S.photoBtn} onClick={() => cameraInputRef.current?.click()}>
                  <Camera size={16} /> Take Photo
                </button>
                <button type="button" style={S.photoBtn} onClick={() => photoInputRef.current?.click()}>
                  <Upload size={16} /> Upload
                </button>
              </div>
            )}

            <div style={S.noteLabel}>📝 Anything else?</div>
            <textarea
              style={S.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="E.g. Need wiring for inverter and two lights."
              rows={3}
            />

            <div style={S.addressBox}>
              <MapPin size={15} color="#16a34a" />
              <span>We'll use your saved service address for this booking.</span>
            </div>

            <button type="button" style={S.continueBtn} onClick={goToSchedule}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Date & Time (2-hour slot) ─────────────────────────────────────── */}
        {step === "schedule" && (
          <>
            <div style={S.summaryBar}>
              <div style={S.summaryBody}>
                <div style={S.summaryTitle}>Wiring Booking Summary</div>
                <div style={S.summaryChipRow}>
                  <span style={S.summaryChip}>
                    {wiringType?.label} — {location === "internal" ? "Internal" : "External"} — {lengthLabel()}
                  </span>
                  <span style={S.summaryDurationChip}>{hasStartingPrice ? "Starting " : ""}₹{grandTotal.toFixed(2)}</span>
                </div>
                {pointsSummary && <div style={S.noteBox}>Points: {pointsSummary}</div>}
                {note.trim() && <div style={S.noteBox}>"{note.trim()}"</div>}
                {hasStartingPrice && (
                  <div style={S.noteBox}>
                    Final wiring quantity and material will be confirmed by the electrician after inspection. Additional wiring/work requires your approval.
                  </div>
                )}
              </div>
              <button type="button" style={S.summaryChangeBtn} onClick={goBack}>
                Change
              </button>
            </div>

            <div style={S.scheduleHead}>
              <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
              <div style={S.legend}>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
              </div>
            </div>

            <div style={S.scheduleColumns}>
              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>1. Select Date</div>
                {loadingAvailability ? (
                  <div style={S.stateBox}>Loading…</div>
                ) : availabilityError ? (
                  <div style={S.errorBox}>{availabilityError}</div>
                ) : (
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button"
                          disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                <div style={S.slotGrid}>
                  {(activeDay?.slots || []).map((slot) => {
                    const state      = getSlotState(slot);
                    const selectable = state === "available";
                    const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                    const selected   = selectedTime === slot.time;
                    return (
                      <button
                        key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        style={{
                          ...S.slotBtn,
                          ...(selected ? S.slotBtnActive : {}),
                          ...(state === "booked" ? S.slotBtnBooked : {}),
                          ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                        }}
                        onClick={() => selectable && pickTime(slot.time, slot._id)}
                      >
                        <div style={S.slotTimeRow}>
                          <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                          {selected && <CheckCircle2 size={13} color="#fff" />}
                        </div>
                        <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                          {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>{isLengthNotSure ? "Visit / Inspection" : "Wiring — Minimum 6m"}</span><span>₹{(isLengthNotSure ? subtotal : wiringBase).toFixed(2)}</span></div>
              {!isLengthNotSure && <div style={S.breakdownRow}><span>Labour</span><span>₹{labour.toFixed(2)}</span></div>}
              {!isLengthNotSure && material > 0 && <div style={S.breakdownRow}><span>Material</span><span>₹{material.toFixed(2)}</span></div>}
              {!isLengthNotSure && pointsAddOn > 0 && <div style={S.breakdownRow}><span>Additional Points</span><span>₹{pointsAddOn.toFixed(2)}</span></div>}
              <div style={S.breakdownRow}><span>Tax &amp; Surcharge</span><span>₹{(tax + surcharge).toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>{hasStartingPrice ? "Starting Price" : "TOTAL"}</span><span>₹{grandTotal.toFixed(2)}</span></div>
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!selectedTime || checkoutLoading ? S.continueBtnDisabled : {}) }}
              disabled={!selectedTime || checkoutLoading}
              onClick={handleCheckout}
            >
              {checkoutLoading ? "Booking..." : isUserLoggedIn() ? "Book Now" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "660px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },

  workTypeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" },
  workTypeCard: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  workTypeIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  workTypeBody: { flex: 1, minWidth: 0 },
  workTypeLabel: { fontSize: "15px", fontWeight: 800, color: "#111827" },
  workTypeDesc: { fontSize: "11.5px", color: "#9ca3af", marginTop: "2px" },

  pointsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "14px", marginBottom: "18px" },
  pointCard: { background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "14px", overflow: "hidden" },
  pointCardActive: { borderColor: "#16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  pointImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f0f2fa" },
  pointBody: { padding: "10px 12px 12px" },
  typeCheckRow: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" },
  typeName: { fontSize: "13px", fontWeight: 800, color: "#111827" },

  lengthCard: { padding: "18px 8px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", fontSize: "15px", fontWeight: 800, color: "#111827", textAlign: "center" },

  customPointsRow: { display: "flex", gap: "12px", marginTop: "4px", flexWrap: "wrap", alignItems: "center" },
  customPointsInput: { flex: "1 1 200px", padding: "12px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "13px", fontFamily: "inherit", boxSizing: "border-box" },

  qtyRow: { display: "flex", alignItems: "center", gap: "10px" },
  qtyBtn: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #e4e7ef", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", flexShrink: 0 },
  qtyBtnP: { width: "26px", height: "26px", borderRadius: "50%", border: "none", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 },
  qtyBtnDisabled: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #eceef5", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "not-allowed", color: "#c1c5cf", flexShrink: 0 },
  qtyText: { minWidth: "18px", textAlign: "center", fontSize: "13px", fontWeight: 700, color: "#111827" },

  hiddenInput: { display: "none" },
  photoBtnRow: { display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "18px" },
  photoBtn: { display: "inline-flex", alignItems: "center", gap: "8px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "11px 18px", borderRadius: "10px" },
  photoPreviewRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" },
  photoPreview: { width: "90px", height: "90px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e5e7eb" },
  addedRemoveBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  noteLabel: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  textarea: { width: "100%", minHeight: "80px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontFamily: "inherit", fontSize: "13px", resize: "vertical", marginBottom: "16px", boxSizing: "border-box" },
  addressBox: { display: "flex", alignItems: "center", gap: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px", padding: "10px 12px", fontSize: "12.5px", color: "#166534", marginBottom: "18px" },

  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
  summaryBody: { minWidth: 0, flex: 1 },
  summaryTitle: { fontSize: "15px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },
  noteBox: { fontSize: "12px", color: "#6b7280", background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "10px", padding: "10px 12px", marginTop: "10px", lineHeight: 1.4 },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  noteFooterBtn: { width: "auto", padding: "13px 24px", marginTop: 0 },
};

export default WiringBookingPage;
